import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createSession, getSession, updateSession, getSchedule } from '../utils/api';
import { getCookie, setCookie, deleteCookie } from '../utils/cookies';
import { useCalendar } from './CalendarContext';
import { useBranding } from './BrandingContext';
import { getPossessionModel, getPossessionFields, fieldStateKey } from '../utils/dataFields';
// CIN-6879: deep-link precedence lives in ONE pure module. The location/step
// decision has to be consulted from three places below (the location-mismatch
// branch, the session-restore guard and the advance effect) - if any of them
// disagreed, the SECOND initializeSession pass (`locations` is in its dep
// array, so it re-runs when the locations fetch lands) would revert the advance.
import { deepLinkReplacesSession, findServiceDeepLinkLocation, resolveDeepLinkTarget } from '../utils/deepLink';

const SessionContext = createContext();

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const { calendarId, locations, locationIdParam, fields } = useCalendar();
  const { widgetConfig } = useBranding();
  const location = useLocation();
  const [sessionId, setSessionId] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [vehicleCustomerRefId, setVehicleCustomerRefId] = useState(null);
  const [vehiclePossessionId, setVehiclePossessionId] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  const [schedule, setSchedule] = useState(null);
  // CIN-6879: the location the hydrated session itself pins (null when it pins
  // none, or when it pins one that no longer resolves). Read by the advance
  // effect so its stand-down rule is byte-for-byte the same one the restore
  // guard uses.
  const hydratedSessionLocationRef = useRef(null);
  // CIN-7133: snapshot of what the hydrated session carried (location,
  // services, booked/cancelled markers), so the advance effect can tell whether
  // a service deep link replaces the session's selection.
  const hydratedSessionRef = useRef(null);
  // CIN-6879: one-shot. Guards a SYNCHRONOUS pair of setState calls, so it is
  // deliberately NEVER re-armed in a cleanup (the re-arm rule applies to latches
  // around async fetches). React 19 StrictMode remounts effects but PRESERVES
  // refs, so the double mount is a no-op - proven by the StrictMode case in
  // SessionContext.test.js. This latch is also exactly what makes Back stick:
  // once armed, handleBack's setCurrentStep(2) is not re-advanced.
  const deepLinkAdvancedRef = useRef(false);
  const [personalInfo, setPersonalInfo] = useState({
    first_name: widgetConfig?.defaults?.contact?.firstName || '',
    last_name: widgetConfig?.defaults?.contact?.lastName || '',
    phone: widgetConfig?.defaults?.contact?.phoneNumber || '',
    email: widgetConfig?.defaults?.contact?.email || '',
    year: widgetConfig?.defaults?.vehicle?.vehicleYear || '',
    make: widgetConfig?.defaults?.vehicle?.vehicleMake || '',
    model: widgetConfig?.defaults?.vehicle?.vehicleModel || '',
    comments: ''
  });

  useEffect(() => {
    if (!calendarId) {
      return;
    }

    // If we have a locationId param but locations aren't loaded yet, wait
    if (locationIdParam && locations.length === 0) {
      return;
    }

    const initializeSession = async () => {
      try {
        const cookieName = `scheduler_session_${calendarId}`;

        // Check for sessionId in query parameters first
        const urlParams = new URLSearchParams(location.search);
        const querySessionId = urlParams.get('sessionId');

        let existingSessionId = querySessionId || getCookie(cookieName);
        let sessionData = null;

        if (existingSessionId) {
          try {
            sessionData = await getSession(calendarId, existingSessionId);
            // If we got a valid session from query param, update the cookie
            if (sessionData && querySessionId) {
              setCookie(cookieName, sessionData.id, 30);
              window.parent.postMessage({ type: 'RESUME_SESSION', data: sessionData }, '*');
            }
          } catch (error) {
            console.error('Error fetching session data:', error);
          }
        }

        if (!sessionData) {
          // Get or create session.  It also tries to recover session by IP address and user agent
          try {
            sessionData = await createSession(calendarId);
            if (sessionData.id) {
              setCookie(cookieName, sessionData.id, 30); // Store for 30 days
              existingSessionId = sessionData.id;
              window.parent.postMessage({ type: 'RESUME_SESSION', data: sessionData }, '*');
            }
          } catch (error) {
            console.error('Failed to create session:', error);
          }
        }

        if (sessionData) {
          if (sessionData.entity_data?.selected_date) {
            // Check if the appointment time has passed
            const appointmentTime = new Date(sessionData.entity_data.selected_date);
            if (appointmentTime < new Date()) {
              // THe appointment we selected is in the past so we need to clear it out.
              sessionData.entity_data.selected_date = null;
            }
          }

          setPhoneVerified(!!sessionData.phone_validated_when);
          setCookie(cookieName, sessionData.id, 30); // Store for 30 days
          setSessionId(sessionData.id);

          // Check if the session is cancelled or expired
          if (sessionData.deleted_when) {
            setCancelled(true);
          }

          // If session has a schedule_id, fetch the schedule details
          if (sessionData.schedule_id) {
            // CIN-5926: a finalized session has a booked schedule. Drop the
            // cookie so a future page load starts a fresh session instead of
            // silently re-attaching to this finalized one. The local React
            // state (sessionId, schedule) is still set so the current tab
            // continues to render the success screen and supports the
            // Edit/Cancel/New flows.
            deleteCookie(cookieName);
            try {
              const scheduleData = await getSchedule(sessionData.id);
              setSchedule(scheduleData);

              if (scheduleData.appointment_naive && new Date(scheduleData.appointment_naive) < new Date()) {
                // The appointment we selected is in the past so we need to clear it out.
                setCancelled(true);
              }
            } catch (error) {
              console.error('Failed to fetch schedule details:', error);
            }
          }

          hydratedSessionLocationRef.current = sessionData.location || null;
          hydratedSessionRef.current = {
            location: sessionData.location || null,
            services: sessionData.services,
            schedule_id: sessionData.schedule_id || null,
            deleted_when: sessionData.deleted_when || null,
          };

          // CIN-6879: one resolver, consulted here and by the restore guard and
          // the advance effect below, so every initializeSession pass agrees.
          const deepLinkTarget = resolveDeepLinkTarget({
            search: location.search,
            locations,
            locationIdParam,
            sessionLocationId: sessionData.location || null,
          });
          const replacesSession = deepLinkReplacesSession({ target: deepLinkTarget, session: sessionData });

          if (locationIdParam && sessionData?.location !== locationIdParam) {
            // We have a location mismatch so send them to the select services page
            const matchingLocation = locations.find(loc => loc.id === locationIdParam);
            if (matchingLocation) {
              setSelectedLocation(matchingLocation);
              // CIN-6879 (e2e finding #2): the deep-link landing is applied ONCE.
              // `deepLinkAdvancedRef` is armed by the advance effect below, which
              // can only run after the FIRST pass has finished hydrating, so this
              // branch always applies in full on that first pass. Every LATER
              // pass (`locations` is in this effect's dep array, so it re-runs
              // when the fetch lands) must leave the step and the selection
              // alone: by then the visitor owns them, and re-applying step 3 over
              // a Back click that landed in the ~50ms window between the advance
              // and this pass silently pushed them forward again.
              if (!deepLinkAdvancedRef.current) {
                // This branch is taken on EVERY pass for a fresh session on a
                // `?location=`/`~` landing URL (sessionData.location is null),
                // and it used to hard-set step 2 and CLEAR selectedServices -
                // which reverted the service deep link. When the URL also
                // carries a service the location offers, land on Date & Time
                // with that service selected instead.
                const landsOnDateTime = deepLinkTarget.step === 3 && deepLinkTarget.location?.id === matchingLocation.id;
                setCurrentStep(landsOnDateTime ? 3 : 2);
                setSelectedServices(landsOnDateTime ? [deepLinkTarget.service] : []);
                setSelectedTimeSlot(null);
              }
            }
          } else {
            // CIN-6784: `locations` is a dependency of this effect, so it runs a
            // second time once the locations fetch lands. Without this, that pass
            // restored the persisted current_step=1 and undid the step the
            // service deep-link effect below had just advanced to 2 - the visitor
            // saw Location -> Services -> Location. A live deep link means the
            // effect will (re)select the offering location, so step 1 is stale
            // here for exactly the same reason it is with a location param.
            // Skipped when the session itself pins a location that resolves,
            // because then the deep-link effect stands down and the session wins.
            const sessionLocationResolves = !!(
              sessionData.location && locations.some(loc => loc.id === sessionData.location)
            );
            const serviceDeepLinkActive =
              !sessionLocationResolves
              && !!findServiceDeepLinkLocation(location.search, locations, locationIdParam);

            // CIN-6879 (e2e finding #2): same one-shot rule as the mismatch
            // branch above - once the deep-link advance has fired, the step
            // belongs to the visitor. A later pass must not restore ANY step
            // over it (neither the deep-link landing nor the persisted
            // current_step), or a Back click that lands before this pass
            // resolves is silently undone. The latch can only be armed after the
            // FIRST pass has finished hydrating, so that pass always applies.
            const stepOwnedByVisitor = deepLinkAdvancedRef.current;

            // CIN-7133: a service deep link that replaces the session's
            // selection lands on Date & Time. Once the visitor owns the step
            // (latch armed), it and the two writes below are SKIPPED rather than
            // falling through to the persisted restore: this pass may still see
            // the stale server copy (the PATCH is debounced), and restoring it
            // put the first-visited service back over the new link. Only when
            // the session's location resolves, i.e. when the services write
            // below also applies; otherwise (fresh session) the advance effect
            // lands it, so step 3 is never painted with nothing selected.
            if (!stepOwnedByVisitor && replacesSession && sessionLocationResolves) {
              setCurrentStep(3);
            } else if (!stepOwnedByVisitor && sessionData.current_step == 1 && (locationIdParam || serviceDeepLinkActive || deepLinkTarget.step === 3)) {
              // Session is valid and location matches (or no locationId param)
              // CIN-6879: the extra `deepLinkTarget.step === 3` term covers a
              // SINGLE-location calendar, where findServiceDeepLinkLocation
              // deliberately returns null (that case is owned by the auto-select
              // effect) but the deep-linked service still resolves.
              setCurrentStep(deepLinkTarget.step === 3 ? 3 : 2);
              if (deepLinkTarget.step === 3 && deepLinkTarget.service) {
                setSelectedServices([deepLinkTarget.service]);
              }
            } else if (!stepOwnedByVisitor && sessionData.current_step) {
              if (sessionData.schedule_id) {
                setCurrentStep(6);
              } else if (sessionData.current_step > 3 && !sessionData.entity_data?.selected_date) {
                setCurrentStep(3);
              } else {
                setCurrentStep(sessionData.current_step);
              }
            }

            if (sessionData.location && locations.length > 0) {
              const location = locations.find(l => l.id === sessionData.location);
              if (location) {
                setSelectedLocation(location);

                if (replacesSession) {
                  if (!stepOwnedByVisitor) setSelectedServices([deepLinkTarget.service]);
                } else if (sessionData.services && Array.isArray(sessionData.services)) {
                  setSelectedServices(location.services.filter(s => sessionData.services.includes(s.id)));
                }
              }
            }

            if (replacesSession) {
              // CIN-7133: the stale slot belonged to the replaced services.
              if (!stepOwnedByVisitor) setSelectedTimeSlot(null);
            } else if (sessionData.entity_data?.selected_date) {
              setSelectedTimeSlot(sessionData.entity_data.selected_date);
            }

            if (sessionData.entity_data?.selected_customer_id) {
              setSelectedCustomerId(sessionData.entity_data.selected_customer_id);
            }

            if (sessionData.entity_data?.selected_vehicle_id) {
              setSelectedVehicleId(sessionData.entity_data.selected_vehicle_id);
            }

            if (sessionData.entity_data?.vehicle_customer_ref_id) {
              setVehicleCustomerRefId(sessionData.entity_data.vehicle_customer_ref_id);
            }

            if (sessionData.entity_data?.vehicle_possession_id) {
              setVehiclePossessionId(sessionData.entity_data.vehicle_possession_id);
            }
          }

          if (sessionData.first_name || sessionData.last_name || sessionData.phone || sessionData.email) {
            setPersonalInfo(prev => ({
              ...prev,
              first_name: sessionData.first_name || '',
              last_name: sessionData.last_name || '',
              phone: sessionData.phone || '',
              email: sessionData.email || '',
              comments: sessionData.entity_data?.comments || '',
              // Rehydrate configured possession fields (fields.possession) from entity_data.possession
              ...getPossessionFields(fields).reduce((acc, field) => {
                const key = fieldStateKey(field);
                acc[key] = sessionData.entity_data?.possession?.[key] || '';
                return acc;
              }, {}),
              // Rehydrate any configured extra data fields (fields.extra)
              ...(fields?.extra || []).reduce((acc, field) => {
                const key = field.id.replace(/^entity_data__/, '');
                acc[key] = sessionData.entity_data?.[key] || '';
                return acc;
              }, {})
            }));
          }
        }
      } catch (error) {
        console.error('Session initialization error:', error);
      }

      setSessionLoading(false);
    };

    initializeSession();
  }, [calendarId, locations, location.search]);

  // Auto-select location if there's only one
  useEffect(() => {
    if (locations.length === 1 && !selectedLocation && !sessionLoading) {
      setSelectedLocation(locations[0]);
      // Skip to step 2 if we're on step 1
      if (currentStep === 1) {
        setCurrentStep(2);
      }
    }
  }, [locations, selectedLocation, sessionLoading, currentStep]);

  // Handle the pinned-location param.
  //
  // CIN-6879 (e2e finding #1): this used to re-parse `location.search` for
  // `locationId`/`location`, which made it blind to the `<cal>~<loc>` path half
  // that CalendarContext already merged into `locationIdParam`. On the `~` form
  // it therefore did nothing, the service auto-select effect below picked a
  // DIFFERENT merchant that happened to offer the service, and its address was
  // painted for ~100ms until the second initializeSession pass corrected it.
  // There is now exactly ONE resolved value (query param > path half) and every
  // location-resolution site in this file consumes it.
  useEffect(() => {
    if (locations.length > 0 && !selectedLocation && !sessionLoading) {
      if (locationIdParam) {
        // Find the location by ID
        const matchingLocation = locations.find(loc => loc.id === locationIdParam);
        if (matchingLocation) {
          setSelectedLocation(matchingLocation);
          // Skip to step 2 if we're on step 1
          if (currentStep === 1) {
            setCurrentStep(2);
          }
        }
      }
    }
  }, [locations, selectedLocation, sessionLoading, currentStep, locationIdParam]);

  // CIN-6784 (Issue C-2): a Reserve with Google service link can arrive as a bare
  // `?service=<id>` with no usable location param. On a multi-location calendar
  // that used to stall on the location step with nothing preselected, and if the
  // visitor then picked a location that doesn't offer the service, the CIN-6441
  // preselect silently no-opped. Auto-select the first location that actually
  // offers the deep-linked service and advance to the services step, where the
  // ServiceSelection preselect then checks it.
  //
  // Placed AFTER the single-location and location-param effects so both of those
  // win when they apply, and gated on !selectedLocation so a resumed session, a
  // param match and a manual pick are all left alone.
  useEffect(() => {
    if (sessionLoading || selectedLocation) {
      return;
    }

    // findServiceDeepLinkLocation also re-checks an explicit location param that
    // resolves (the effect above owns that case, and both effects run in the same
    // commit, so selectedLocation is still stale here) and returns null when no
    // location offers the service - then the visitor is left on the location step
    // exactly as today rather than sent to one that would dead-end.
    //
    // CIN-6879 (e2e finding #1): the resolved `locationIdParam` is passed in, so
    // that stand-down check sees the `<cal>~<loc>` path half too. Without it this
    // effect briefly selected another merchant on the `~` form.
    const offeringLocation = findServiceDeepLinkLocation(location.search, locations, locationIdParam);
    if (!offeringLocation) {
      return;
    }

    setSelectedLocation(offeringLocation);
    if (currentStep === 1) {
      setCurrentStep(2);
    }
  }, [locations, locationIdParam, selectedLocation, sessionLoading, currentStep, location.search]);

  // CIN-6879: land a Reserve with Google SERVICE link on the Date & Time step
  // with the service already selected, instead of on the services picker.
  //
  // Placed AFTER all three location effects above so whichever of them owns the
  // location wins first; this effect only confirms that the location actually
  // committed matches the resolver's target and then advances. It never runs
  // before hydration settles, never advances past 3, never touches
  // selectedTimeSlot, and adds NO API call - the debounced updateSessionData
  // below PATCHes location/services/current_step: 3 for free, which is what
  // makes a reload come back to Date & Time.
  useEffect(() => {
    if (sessionLoading || deepLinkAdvancedRef.current) {
      return;
    }

    const target = resolveDeepLinkTarget({
      search: location.search,
      locations,
      locationIdParam,
      sessionLocationId: hydratedSessionLocationRef.current,
    });
    if (target.step !== 3) {
      return;
    }

    // Wait for the location effect that owns this target to commit. Until then
    // the latch stays un-armed so the advance is not silently lost.
    if (!selectedLocation || selectedLocation.id !== target.location.id) {
      return;
    }

    deepLinkAdvancedRef.current = true;
    // CIN-7133: a link for a different selection than the resumed session's
    // replaces it outright (services, stale time slot, step 3). Booked,
    // cancelled and same-single-service sessions keep the CIN-6879 rules.
    if (deepLinkReplacesSession({ target, session: hydratedSessionRef.current })) {
      setSelectedServices([target.service]);
      setSelectedTimeSlot(null);
      if (currentStep !== 3) setCurrentStep(3);
    } else {
      if (selectedServices.length === 0) {
        setSelectedServices([target.service]);
      }
      // `<= 2` leaves a resumed session restored to step 4/5/6 alone.
      if (currentStep <= 2) {
        setCurrentStep(3);
      }
    }
  }, [locations, locationIdParam, selectedLocation, selectedServices, sessionLoading, currentStep, location.search]);

  // Update session data in API
  const updateSessionData = async () => {
    if (!sessionId || !calendarId) return;

    let queryParams = new URLSearchParams();
    if (document.referrer) {
      queryParams = new URLSearchParams(document.referrer);
    }

    if (!queryParams.get('utm_source') && !queryParams.get('utm_medium') && !queryParams.get('utm_campaign')) {
      // If there are no UTM params in the referrer URL, try to get them from the current URL (in case they were lost during navigation)
      const currentUrlParams = new URLSearchParams(window.location.search);
      queryParams = currentUrlParams;
    }

    try {
      const sessionData = {
        location: selectedLocation?.id || null,
        services: selectedServices.map(s => typeof s === 'object' ? s.id : s),
        first_name: personalInfo.first_name !== '' ? personalInfo.first_name : null,
        last_name: personalInfo.last_name !== '' ? personalInfo.last_name : null,
        phone: personalInfo.phone,
        email: personalInfo.email,
        current_step: currentStep,
        referrer_url: document.referrer || null,
        host_url: window.location.href || null,
        utm_source: queryParams.get('utm_source') || null,
        utm_medium: queryParams.get('utm_medium') || null,
        utm_campaign: queryParams.get('utm_campaign') || null,
        entity_data: {
          selected_date: selectedTimeSlot || null,
          possession_model: getPossessionModel(fields),
          // Configured possession field values, keyed by field id
          possession: getPossessionFields(fields).reduce((acc, field) => {
            const key = fieldStateKey(field);
            acc[key] = personalInfo[key] || null;
            return acc;
          }, {}),
          selected_customer_id: selectedCustomerId || null,
          selected_vehicle_id: selectedVehicleId || null,
          vehicle_customer_ref_id: vehicleCustomerRefId || null,
          vehicle_possession_id: vehiclePossessionId || null,
          comments: personalInfo.comments || null,
          referrer: document.referrer || null,
          // All configured extra data fields (fields.extra)
          ...(fields?.extra || []).reduce((acc, field) => {
            const key = field.id.replace(/^entity_data__/, '');
            acc[key] = personalInfo[key] || null;
            return acc;
          }, {})
        }
      };

      const response = await updateSession(calendarId, sessionId, sessionData);
      window.parent.postMessage({ type: 'UPDATE_SESSION', data: response }, '*');
    } catch (error) {
      console.error('Failed to update session:', error);
    }
  };

  // Debounced session update
  useEffect(() => {
    if (!sessionId || !calendarId) {
      return;
    }

    const timeoutId = setTimeout(() => {
      updateSessionData();
    }, 500); // 500ms debounce delay

    // Cleanup function to cancel the timeout if dependencies change
    return () => clearTimeout(timeoutId);
  }, [currentStep, selectedLocation, selectedServices, selectedTimeSlot, personalInfo, sessionId, calendarId, selectedCustomerId, selectedVehicleId, vehicleCustomerRefId, vehiclePossessionId]);

  const value = {
    sessionId,
    sessionLoading,
    currentStep,
    setCurrentStep,
    selectedLocation,
    setSelectedLocation,
    selectedServices,
    setSelectedServices,
    selectedTimeSlot,
    setSelectedTimeSlot,
    personalInfo,
    setPersonalInfo,
    updateSessionData,
    phoneVerified,
    setPhoneVerified,
    selectedCustomerId,
    setSelectedCustomerId,
    selectedVehicleId,
    setSelectedVehicleId,
    vehicleCustomerRefId,
    setVehicleCustomerRefId,
    vehiclePossessionId,
    setVehiclePossessionId,
    cancelled,
    setCancelled,
    schedule,
    setSchedule
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};